
import { useEffect, useState } from 'react';
import moment from 'moment';
import { Autocomplete, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, IconButton, Button, InputAdornment, Tooltip, } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { labelMap } from '..';
import { regexConstants } from '../../../../constants/regEx';
import { backendDateFormat, fieldDateFormat } from '../../../../constants';
import { NoRecordFound } from '../../../../shared_elements';
import { trackActivity } from '../../../../utils/mixpanel';
import { RangeContinuityInfo } from '../../../elements';
import ImportModal from '../../../elements/ImportModal';
import { exportToExcel, formatExcelDate, validateExcelDate } from '../../../../utils/helper';
import { numberWithCommas } from '../../../../utils';

const rangeConditionMap = { "less than equal to": { value: 1, label: "Less Than Equal To", }, "in between equal to": { value: 2, label: "In Between Equal To", }, "greater than equal to": { value: 3, label: "Greater Than Equal To", }, };
const RangeConditionTable = ({ reserveRates, reserveBasedOn, tableData, pltConstants, modeType, onMultiFieldChange, onMultiDelete, onAddMultiRow, error, setError, onImportExcel, excelData, setExcelData }) => {
  const [continuityError, setContinuityError] = useState({})
  const [importDetails, setImportDetails] = useState({ modal: false, data: null })
  const handleItemClicked = (page_type) => { trackActivity('Item Clicked', { page_title: `Range Conditions`, page_type: page_type, }) }
  const rateByMap = { 2: "Flight Hours", 3: "Flight Cycles", 6: "Block Hours", 8: reserveRates?.interval?.label ? reserveRates?.interval?.label : "" };
  const exportNameMap = { 2: "Flight_Hours", 3: "Flight_Cycles", 6: "Block_Hours",  8: reserveRates?.interval?.value === 2 ? "Flight_Hours" : "Flight_Cycles" };
  const requiredHeaders = reserveBasedOn !== 8 ? ["range conditions", "min", "max", "rate", "rate type"] : ["range conditions", "min", "max", "rate"];
  const headerMap = {
    "range condition": "range conditions",
    "range conditions": "range conditions",
    "min": "min",
    "minimum": "min",
    "max": "max",
    "maximum": "max",
    rate: "rate",
    [`rate/${reserveRates?.interval?.label?.toLowerCase()}`]: "rate",
    [`rate/${rateByMap[reserveBasedOn]?.toLowerCase()}`]: "rate",
    "rate type": "rate type",
    "rental rate type": "rate type",
    "escalation": "escalation (%)",
    "escalation (%)": "escalation (%)",
    "esc start": "esc start",
  };
  const rateTypeMap = { "FIXED": { label: "FIXED", value: 1 }, "PER UNIT": { label: "PER UNIT", value: 2 }, };
  const samplePayload = {
    reserve_rate_based_on : reserveBasedOn,
    ...(reserveBasedOn === 8 && { interval: reserveRates?.interval?.value  || null, }),
  }
  const type2Data = reserveRates?.pbh_condition?.filter(item => item.type === 2);
  const type3Data = reserveRates?.pbh_condition?.filter(item => item.type === 3);
  const updatedRateType = pltConstants?.constant_types?.filter(item => item.type === 'rental_rate_type')?.map(item => ({ ...item, label: item.label?.toUpperCase() }));
  const onFocusChange = (type, rowIndex, field) => { setError(prev => ({ ...prev, [type]: { ...prev?.[type], [rowIndex]: { ...prev?.[type]?.[rowIndex], [field]: "" } } })); };
  const validateContinuousRanges = (rows) => {
    if (!rows?.length) return true;
    const normalize = (val, isInfinity = false) => {
      if (isInfinity) return Infinity;
      if (val === undefined || val === null || val === "") return null;
      const num = Number(val);
      return isNaN(num) ? null : Number(num.toFixed(2));
    };
    const formatted = rows?.map(r => ({ min: normalize(r.min), max: normalize(r.max, r.ratio_range === 3), }))?.filter(r => r.min !== null && r.max !== null)?.sort((a, b) => a.min - b.min);
    if (!formatted.length) return true;
    for (let i = 0; i < formatted.length; i++) {
      const current = formatted[i];
      if (current.min > current.max) return true;
      if (current.max === Infinity && i !== formatted.length - 1) return true;
      if (i < formatted.length - 1) {
        const next = formatted[i + 1];
        const diff = Number((next.min - current.max).toFixed(2));
        if (diff < 0) return true;
        if (diff > 0.01) return true;
      }
    }
    return false;
  };
  useEffect(() => {
    if (!tableData?.length) return;
    const isInvalid = validateContinuousRanges(tableData);
    const key = Number( reserveBasedOn);
    setContinuityError(prev => ({ ...prev, [key]: isInvalid }));
  }, [tableData, reserveBasedOn]);
  const getDisableExport = (data) => { return !data?.length; };
  const exportData = reserveBasedOn === 2 ? type2Data : reserveBasedOn === 3 ? type3Data : reserveRates?.pbh_condition;
  const isButtonEnabled = reserveBasedOn === 8 ? !reserveRates?.interval?.value || !reserveRates?.allow_decimal?.value || !reserveRates?.rounding?.value : false;


  const transformExcelData = (data) => {
    return data.map((row, index) => {
      const rateValue = String(row["rate"] || "").replace(/[^0-9.]/g, "");
      const rateTypeRaw = String(row["rate type"] || "").toUpperCase().trim();
      const rangeCondition = rangeConditionMap[String(row["range conditions"] || "").toLowerCase().trim() ];
      return {
        ratio_range: rangeCondition?.value,
        range_label: row["range conditions"],
        type: reserveBasedOn,
        rate: Number(rateValue) || 0,
        esc_percentage: reserveRates?.table_based_esc ? Number(row["escalation (%)"] || 0) : 0,
        min: Number(row["min"]) || 0,
        max: row["max"] === "∞" ? 0 : Number(row["max"]) || 0,
        esc_date: reserveRates?.table_based_esc ? formatExcelDate(row["esc start"] || row["Esc Start"]) : null,
        ...(reserveBasedOn !== 8 && { rental_rate_type: rateTypeMap[rateTypeRaw] || null, }),
      };
    });
  };

  const validateExcelData = (rows) => {
    const isValidTwoDecimal = (value) => { return /^\d+(\.\d{1,2})?$/.test(String(value).trim()); };
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rate = String(row["rate"] || "").replace(/[^0-9.]/g, "");
      const rateTypeRaw = String(row["rate type"] || "").toUpperCase().trim();
      const escDate = row["esc start"] || row["Esc Start"];
      if (!row["range conditions"] || !rate || row["min"] === "" || row["max"] === "" || row["min"] === null || row["max"] === null) {
        return `Invalid data at row ${i + 2}. Please set format like sample file.`;
      }
      if (!isValidTwoDecimal(row["min"])) { return `Min at row ${i + 2} accepts maximum 2 decimal places only.`; }
      if (row["max"] !== "∞" && !isValidTwoDecimal(row["max"])) { return `Max at row ${i + 2} accepts maximum 2 decimal places only.`; }
      if (!isValidTwoDecimal(rate)) { return `Rate at row ${i + 2} accepts maximum 2 decimal places only.`; }
      if ( reserveRates?.table_based_esc && !isValidTwoDecimal(row["escalation (%)"] || 0) ) { return `Escalation (%) at row ${ i + 2 } accepts maximum 2 decimal places only.`; }
      if (reserveBasedOn !== 8 && !rateTypeMap[rateTypeRaw]) { return `Invalid or missing Rate Type at row ${i + 2}. Allowed: FIXED / PER UNIT`; }
      if (escDate && !validateExcelDate(escDate)) { return `Invalid Esc Start date at row ${i + 2}.`; }
    }
    return null;
  };

  return (
    <>
      <Box >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box display="flex" alignItems="center" gap={0.2}>
            <Typography className='section-header-text'> {labelMap[reserveBasedOn]} </Typography>
            <Tooltip placement='left' slotProps={{ tooltip: { sx: { bgcolor: "white", color: "black", boxShadow: 2, border: "1px solid #ddd" } }, arrow: { sx: { color: "white" } } }} title={<RangeContinuityInfo />} arrow > <InfoIcon fontSize='small' color='primary' /> </Tooltip>
            <span style={{ display: "flex", alignItems: "center", marginTop: "-5px" }}>:</span>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", }}>
            <>
            <Button disabled={isButtonEnabled || modeType === "view"} onClick={() => { setImportDetails({ modal: true }); handleItemClicked('Import Button - RangeConditionTable') }}>Import</Button>
              <Button sx={{ pointerEvents: !reserveRates?.pbh_condition?.length ? "not-allowed" : "pointer" }} disabled={getDisableExport(exportData) || isButtonEnabled}
                onClick={() => { handleItemClicked('Export Button - RangeConditionTable'); exportToExcel({
                  data: exportData,
                  fileName: `${exportNameMap[reserveBasedOn]}_${reserveBasedOn === 8 ? "Range_Based_Ratio" : "Based_Rate"}`,
                  sheetName: `${reserveBasedOn !== 8 ? "Rate Table" : "Ratio Table"}`,
                  columns: {
                    "Range Conditions": "range_label",
                    "Min": "min",
                    "Max": (row) => (row?.ratio_range === 3 && row.max === 0 ? "∞" : row.max),
                    ...(reserveBasedOn !== 8 && { "Rate Type": (row) => row?.rental_rate_type?.label || "", }),
                    [`Rate${rateByMap[reserveBasedOn] ? `/${rateByMap[reserveBasedOn]}` : ""}`]: (row) => `${reserveRates?.currency?.symbol || "$"} ${row.rate}`,
                    ...(reserveRates?.table_based_esc && { "Escalation (%)": "esc_percentage", "Esc Start": (row) =>row.esc_date ? moment(row.esc_date).format("DD-MM-YYYY") : null }),
                  }
                }) }}>Export</Button>
            </>
          </Box>
        </Box>
      </Box>
      <TableContainer component={Paper} className="scrollable-table rental-scrollable-table-container">
        <Table size="small" className="mui-table-format" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Range Conditions</TableCell>
              <TableCell>MIN*</TableCell>
              <TableCell>MAX*</TableCell>
              {reserveBasedOn === 8 ? null : <TableCell>Rate Type*</TableCell>}
              <TableCell>{`Rate${rateByMap[reserveBasedOn] ? `/${rateByMap[reserveBasedOn]}` : ""}*`}</TableCell>
              <TableCell>Escalation{reserveRates?.table_based_esc ? "*" : ""}</TableCell>
              <TableCell>Esc Start{reserveRates?.table_based_esc ? "*" : ""}</TableCell>
              {modeType !== 'view' ?
                <TableCell className='sticky-action-header' align='center'>Action</TableCell> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData?.length > 0 && tableData?.map((row, index) => {
              const globalIndex = reserveRates?.pbh_condition?.indexOf(row);
              const rowError = error?.[row.type]?.[globalIndex];
              return (
                <TableRow key={index}>
                  <TableCell className="nowrap-cell range-cell">{row?.range_label}</TableCell>
                  <TableCell >
                    <TextField
                      defaultValue={0}
                      value={row?.ratio_range === 1 ? "0" : (modeType === "view" && row?.min !== '' && row?.min !== null && row?.min !== undefined ? numberWithCommas(row?.min) : row.min)}
                      disabled={row?.ratio_range === 1 || modeType === "view"}
                      size="small"
                      slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, }}
                      onChange={(e) => { regexConstants.decimalnodotstart.test(e.target.value) ? onMultiFieldChange("pbh_condition", globalIndex, 'min', e.target.value,) : e.preventDefault() }}
                      error={Boolean(rowError?.min) || false}
                      helperText={rowError?.min || ""}
                      onFocus={() => onFocusChange(row.type, globalIndex, "min")}
                    // onBlur={handleRangeBlur}
                    />
                  </TableCell>
                  <TableCell >
                    <TextField
                      defaultValue={0}
                      value={row?.ratio_range === 3 ? "∞" : (modeType === "view" && row?.max !== '' && row?.max !== null && row?.max !== undefined ? numberWithCommas(row?.max) : row.max)}
                      disabled={row?.ratio_range === 3 || modeType === "view"}
                      size="small"
                      slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, }}
                      onChange={(e) => { regexConstants.decimalnodotstart.test(e.target.value) ? onMultiFieldChange("pbh_condition", globalIndex, 'max', e.target.value,) : e.preventDefault() }}
                      error={Boolean(rowError?.max) || false}
                      helperText={rowError?.max || ""}
                      onFocus={() => onFocusChange(row.type, globalIndex, "max")}
                    // onBlur={handleRangeBlur}
                    />
                  </TableCell>
                  {reserveBasedOn !== 8 ?
                    <TableCell>
                      <Autocomplete
                        style={{ width: "170px" }}
                        fullWidth
                        disablePortal
                        disableClearable
                        className='custom-autocomplete'
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        options={updatedRateType}
                        disabled={modeType === 'view'}
                        getOptionLabel={(option) => option.label || ""}
                        value={row?.rental_rate_type}
                        onChange={(e, value) => onMultiFieldChange("pbh_condition", globalIndex, 'rental_rate_type', value)}
                        renderInput={(params) => (
                          <TextField
                            required
                            fullWidth
                            {...params}
                            margin='normal'
                            size='small'
                            slotProps={{ inputLabel: { shrink: true } }}
                            disabled={modeType === 'view'}
                          />
                        )}
                      />
                    </TableCell> : null}
                  <TableCell >
                    <TextField
                      defaultValue={0}
                      value={modeType === 'view' && row?.rate !== '' && row?.rate !== null && row?.rate !== undefined ? numberWithCommas(row?.rate) : row.rate}
                      size="small"
                      onChange={(e) => { regexConstants.decimalnodotstart.test(e.target.value) ? onMultiFieldChange("pbh_condition", globalIndex, 'rate', e.target.value) : e.preventDefault() }}
                      disabled={modeType === 'view'}
                      slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">{reserveRates?.currency?.symbol}</InputAdornment>) } }}
                      error={Boolean(rowError?.rate) || false}
                      helperText={rowError?.rate || ""}
                      onFocus={() => onFocusChange(row.type, globalIndex, "rate")}
                    />
                  </TableCell>
                  <TableCell >
                    <TextField
                      slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { maxLength: 5, },
                        input: { endAdornment: (<InputAdornment position="end">%</InputAdornment>) }
                      }}
                      defaultValue={0}
                      value={row.esc_percentage}
                      size="small"
                      onChange={(e) => { regexConstants.percantageRegExp.test(e.target.value) ? onMultiFieldChange("pbh_condition", globalIndex, 'esc_percentage', e.target.value) : e.preventDefault() }}
                      disabled={modeType === 'view' || !reserveRates?.table_based_esc}
                      error={Boolean(rowError?.esc_percentage) || false}
                      helperText={rowError?.esc_percentage || ""}
                      onFocus={() => onFocusChange(row.type, globalIndex, "esc_percentage")}
                    />
                  </TableCell>
                  {/* <TableCell >
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        value={row?.esc_date ? moment(row?.esc_date) : null}
                        onChange={(data, value) => onMultiFieldChange("pbh_condition", row.id, 'esc_date', moment(data).format(backendDateFormat),)}
                        slotProps={{
                          textField: {
                            InputLabelProps: { shrink: true },
                            size: "small",
                            margin: "normal",
                            helperText: false,
                            error: ""
                          },
                        }}
                        format={fieldDateFormat}
                        disabled={modeType === 'view' || !reserveRates?.table_based_esc}
                        error={Boolean(rowError?.esc_date)|| false}
                        helperText={rowError?.esc_date || ""}
                        onFocus={() => onFocusChange(row.type, row.id, "esc_date")}
                      />
                    </LocalizationProvider>
                  </TableCell> */}
                  <TableCell >
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        value={row?.esc_date ? moment(row?.esc_date) : null}
                        onChange={(data) => {
                          const isValid = moment(data)?.isValid();
                          const formattedDate = isValid ? moment(data).format(backendDateFormat) : null;
                          onMultiFieldChange("pbh_condition", globalIndex, 'esc_date', formattedDate)
                        }}
                        slotProps={{
                          popper: { placement: 'bottom-end' },
                          textField: {
                            required: true,
                            error: Boolean(rowError?.esc_date) || false,
                            helperText: rowError?.esc_date || "",
                            InputLabelProps: { shrink: true },
                            size: "small",
                            margin: "normal",
                            onFocus: () => onFocusChange(row.type, globalIndex, "esc_date"),
                            fullWidth: true,
                          },
                          inputProps: {
                            style: {
                              padding: "8.5px 14px",
                              textAlign: "left"
                            }
                          },
                        }}
                        format={fieldDateFormat}
                        disabled={modeType === 'view' || !reserveRates?.table_based_esc}
                        sx={{ width: "100%", minWidth: "160px", }}
                        error={Boolean(rowError?.esc_date) || false}
                        helperText={rowError?.esc_date || ""}
                        onFocus={() => onFocusChange(row.type, globalIndex, "esc_date")}
                      />
                    </LocalizationProvider>
                  </TableCell>
                  {modeType !== "view" ? <TableCell align="center" sx={{ verticalAlign: "middle", p: 0 }}>
                    <Tooltip title="Delete">
                      <IconButton disableRipple size="small" sx={{ cursor: "pointer", }} color="error" onClick={() => { onMultiDelete("pbh_condition", globalIndex); handleItemClicked('DeleteIcon') }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell> : null}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {tableData?.length === 0 && <NoRecordFound />}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
        {modeType !== 'view' ?
          <Button variant='outlined' className='btn-primary' disabled={modeType === 'view'} size='small' onClick={() => { handleItemClicked('Add Row Button'); onAddMultiRow() }} >Add Range Condition</Button>
          : null}
        {continuityError?.[Number( reserveBasedOn)] === true && <Box className="warning-text" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><WarningAmberIcon sx={{ fontSize: 16, color: "#a78701" }} />It’s recommended to define ranges in a continuous sequence. Refer to the info icon.</Box>}
      </Box>

      {importDetails?.modal ? <ImportModal
        requiredHeaders={requiredHeaders}
        headerMap={headerMap}
        transformExcelData={transformExcelData}
        validateExcelData={validateExcelData}
        sampleExportName={`Sample_${exportNameMap[reserveBasedOn]}_${reserveBasedOn === 8 ? "Range_Based_Ratio" : "Based_Rate"}`}
        excelData={excelData}
        setExcelData={setExcelData}
        importDetails={importDetails}
        onImportExcel={onImportExcel}
        samplePayload={samplePayload}
        onToggleModal={() => setImportDetails({ modal: false, data: null })} /> : null}
    </>

  );
};

export default RangeConditionTable;


